import React, { useState } from 'react';
import { Phone, Clock, Shield, CheckCircle2, Building2, Stethoscope } from 'lucide-react';
import SEO from '../components/SEO';
import Button from '../components/Button';
import { contactData } from '../data/contactData';

export default function Contact({ onOpenConsultation }) {
  const [selectedMapLocation, setSelectedMapLocation] = useState('clinic');
  const [formState, setFormState] = useState({
    fullName: '',
    phone: '',
    email: '',
    relationship: 'Family Member / Caregiver',
    service: 'Comprehensive Mental Healthcare',
    urgency: 'Standard Enquiry (Within 24 Hours)',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch('https://formsubmit.co/ajax/contact@umedrehabilitation.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: 'New Website Consultation Enquiry - UMED Rehabilitation Centre',
          _template: 'table',
          _captcha: 'false',
          'Full Name': formState.fullName,
          'Phone Number': formState.phone,
          'Email Address': formState.email || 'Not Provided',
          'Relationship to Patient': formState.relationship,
          'Service Needed': formState.service,
          'Urgency Level': formState.urgency,
          'Message / Brief Context': formState.message || 'No additional message provided',
          'Submitted Date & Time': new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
        })
      });
    } catch (err) {
      console.error('FormSubmit submission error:', err);
    } finally {
      setIsSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="bg-canvas">
      <SEO
        title="Contact & Admissions | Connect with UMED Hospital & Clinic"
        description="Reach out to UMED Mental Hospital & Rehabilitation Centre and UMED Clinic in Miraj. 24/7 psychiatric emergency helpline +91 9226999705, Clinic line +91 8600511455, and Google Maps directions."
      />

      {/* Hero */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 bg-[#123B5D] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0F766E]/30 via-[#123B5D] to-[#123B5D] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-[#0F766E] border border-[#2A9D8F]/30">
              <Shield className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span className="text-xs uppercase tracking-widest text-white font-semibold">
                Confidential & Caring Support
              </span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-normal leading-tight">
              We Are Here to <span className="text-[#2A9D8F]">Listen & Guide</span> You.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#D9E8E5] leading-relaxed">
              Our clinical admissions team and outpatient clinic are ready to answer questions, discuss psychiatric or addiction concerns, and coordinate seamless care for you and your loved ones.
            </p>
          </div>
        </div>
      </section>

      {/* Main Contact Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

            {/* Left Column: Contact Details & Both Locations */}
            <div className="lg:col-span-5 space-y-6">

              {/* Direct Info Cards */}
              <div className="space-y-5">

                {/* Direct Helplines & Email Card */}
                <div className="p-6 rounded-2xl bg-white border border-[#D9E8E5] shadow-card space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-[#F5FAF9] text-[#0F766E] border border-[#D9E8E5]">
                      <Phone className="w-5 h-5 text-[#0F766E]" />
                    </div>
                    <h4 className="font-serif text-lg text-[#123B5D] font-normal">
                      Direct Helplines & Support
                    </h4>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-[#D9E8E5] text-xs sm:text-sm">
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[#64748B] block">
                        24/7 Rehabilitation & Emergency Helpline:
                      </span>
                      <a
                        href={`tel:${contactData.phones[0].raw}`}
                        className="text-base font-bold text-[#0F766E] hover:text-[#123B5D] flex items-center gap-1.5 mt-0.5"
                      >
                        <Phone className="w-4 h-4 text-[#2A9D8F]" />
                        {contactData.phones[0].number}
                      </a>
                    </div>

                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[#64748B] block">
                        OPD Clinic & Counselling Helpline:
                      </span>
                      <a
                        href={`tel:${contactData.phones[1].raw}`}
                        className="text-base font-bold text-[#0F766E] hover:text-[#123B5D] flex items-center gap-1.5 mt-0.5"
                      >
                        <Phone className="w-4 h-4 text-[#2A9D8F]" />
                        {contactData.phones[1].number}
                      </a>
                    </div>

                    <div className="pt-2 border-t border-[#D9E8E5] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[#64748B]">
                      <div>
                        <span className="font-semibold text-[#123B5D] block">Official Email:</span>
                        <a href="mailto:contact@umedrehabilitation.com" className="text-[#0F766E] hover:underline font-medium break-all">contact@umedrehabilitation.com</a>
                      </div>
                      <div>
                        <span className="font-semibold text-[#123B5D] block">Direct Email:</span>
                        <a href="mailto:pawarsanad.sp@gmail.com" className="text-[#0F766E] hover:underline font-medium break-all">pawarsanad.sp@gmail.com</a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location 1: UMED Rehabilitation Centre */}
                <div className="p-6 rounded-2xl bg-white border border-[#D9E8E5] shadow-card space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-[#F5FAF9] text-[#0F766E] border border-[#D9E8E5]">
                        <Building2 className="w-5 h-5 text-[#0F766E]" />
                      </div>
                      <h4 className="font-serif text-lg text-[#123B5D] font-normal">
                        UMED Rehabilitation Centre
                      </h4>
                    </div>
                    <span className="px-2.5 py-1 text-[10px] font-bold rounded-full bg-[#123B5D] text-white flex-shrink-0">
                      24/7 Campus
                    </span>
                  </div>

                  <p className="text-xs text-[#64748B] leading-relaxed pt-1">
                    {contactData.address.line1}, {contactData.address.line2}, {contactData.address.city}, {contactData.address.state} - {contactData.address.pincode}
                  </p>

                  <div className="pt-2 border-t border-[#D9E8E5] flex items-center justify-between text-xs text-[#64748B]">
                    <div className="flex items-center gap-1.5 font-medium text-[#123B5D]">
                      <Clock className="w-3.5 h-3.5 text-[#0F766E]" />
                      <span><strong>Open 24 Hours / 7 Days</strong></span>
                    </div>
                    <a href={`tel:${contactData.phones[0].raw}`} className="text-[#0F766E] font-bold hover:underline">
                      Call Campus
                    </a>
                  </div>
                </div>

                {/* Location 2: UMED Clinic & Counselling Centre */}
                <div className="p-6 rounded-2xl bg-white border-2 border-[#0F766E]/30 shadow-card space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-[#F5FAF9] text-[#0F766E] border border-[#D9E8E5]">
                        <Stethoscope className="w-5 h-5 text-[#0F766E]" />
                      </div>
                      <h4 className="font-serif text-lg text-[#123B5D] font-normal">
                        UMED Clinic & Counselling Centre
                      </h4>
                    </div>
                    <span className="px-2.5 py-1 text-[10px] font-bold rounded-full bg-[#0F766E] text-white flex-shrink-0">
                      OPD Clinic
                    </span>
                  </div>

                  <p className="text-xs text-[#64748B] leading-relaxed pt-1">
                    {contactData.clinicAddress.fullAddress}
                  </p>

                  <div className="pt-2 border-t border-[#D9E8E5] flex items-center justify-between text-xs text-[#64748B]">
                    <div className="flex items-center gap-1.5 font-medium text-[#123B5D]">
                      <Clock className="w-3.5 h-3.5 text-[#0F766E]" />
                      <span><strong>Mon–Fri: 12:00 PM – 7:00 PM</strong></span>
                    </div>
                    <a href={`tel:${contactData.phones[1].raw}`} className="text-[#0F766E] font-bold hover:underline">
                      Call OPD Desk
                    </a>
                  </div>
                </div>

              </div>
            </div>

            {/* Right Column: Confidential Consultation Form */}
            <div className="lg:col-span-7">
              <div className="p-8 sm:p-10 rounded-3xl bg-white border border-[#D9E8E5] shadow-elevated">
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-2 w-2 rounded-full bg-[#0F766E]"></span>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#0F766E]">
                    Confidential Consultation Request
                  </span>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#123B5D] font-normal mb-6">
                  Request an Evaluation or Callback
                </h3>

                {submitted ? (
                  <div className="py-16 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#F5FAF9] text-[#0F766E] flex items-center justify-center mx-auto mb-4 border border-[#D9E8E5]">
                      <CheckCircle2 className="w-8 h-8 text-[#0F766E]" />
                    </div>
                    <h4 className="font-serif text-2xl text-[#123B5D] mb-2">
                      Inquiry Received With Care
                    </h4>
                    <p className="text-xs sm:text-sm text-[#64748B] max-w-md mx-auto mb-6 leading-relaxed">
                      Thank you for trusting UMED. An admissions counselor or clinical coordinator will reach out to <strong className="text-[#123B5D]">{formState.phone}</strong> promptly.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); }}
                      className="px-6 py-2.5 rounded-full bg-[#0F766E] text-white text-xs font-semibold hover:bg-[#123B5D] transition"
                    >
                      Submit Another Inquiry
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-[#18323A] uppercase tracking-wider mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          required
                          value={formState.fullName}
                          onChange={(e) => setFormState({ ...formState, fullName: e.target.value })}
                          placeholder="Your full name"
                          className="w-full px-4 py-3 rounded-xl border border-[#D9E8E5] text-xs sm:text-sm focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition bg-[#F5FAF9]/30 text-[#18323A]"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-[#18323A] uppercase tracking-wider mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={formState.phone}
                          onChange={(e) => setFormState({ ...formState, phone: e.target.value })}
                          placeholder="+91 9226999705 / +91 8600511455"
                          className="w-full px-4 py-3 rounded-xl border border-[#D9E8E5] text-xs sm:text-sm focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition bg-[#F5FAF9]/30 text-[#18323A]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-[#18323A] uppercase tracking-wider mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formState.email}
                          onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                          placeholder="email@example.com"
                          className="w-full px-4 py-3 rounded-xl border border-[#D9E8E5] text-xs sm:text-sm focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition bg-[#F5FAF9]/30 text-[#18323A]"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-[#18323A] uppercase tracking-wider mb-2">
                          Relationship to Patient
                        </label>
                        <select
                          name="relationship"
                          value={formState.relationship}
                          onChange={(e) => setFormState({ ...formState, relationship: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-[#D9E8E5] text-xs sm:text-sm focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition bg-[#F5FAF9]/30 text-[#18323A]"
                        >
                          <option value="Self">Self (Individual seeking support)</option>
                          <option value="Parent">Parent</option>
                          <option value="Spouse">Spouse / Partner</option>
                          <option value="Sibling">Sibling / Family Member</option>
                          <option value="Friend / Colleague">Friend / Colleague</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-[#18323A] uppercase tracking-wider mb-2">
                          Service Needed
                        </label>
                        <select
                          name="service"
                          value={formState.service}
                          onChange={(e) => setFormState({ ...formState, service: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-[#D9E8E5] text-xs sm:text-sm focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition bg-[#F5FAF9]/30 text-[#18323A]"
                        >
                          <option value="Comprehensive Mental Healthcare">Mental Healthcare (Depression/Mood/Psychosis)</option>
                          <option value="Deaddiction & Detoxification">Deaddiction & Detoxification</option>
                          <option value="Psychiatric Rehabilitation">Psychiatric Rehabilitation</option>
                          <option value="Clinic OPD Consultation & Counselling">Clinic OPD Consultation & Counselling</option>
                          <option value="Family Support Program">Family Support Program</option>
                          <option value="Emergency Psychiatric Triage">Emergency Psychiatric Triage</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-[#18323A] uppercase tracking-wider mb-2">
                          Urgency Level
                        </label>
                        <select
                          name="urgency"
                          value={formState.urgency}
                          onChange={(e) => setFormState({ ...formState, urgency: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-[#D9E8E5] text-xs sm:text-sm focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition bg-[#F5FAF9]/30 text-[#18323A]"
                        >
                          <option value="Standard Enquiry (Within 24 Hours)">Standard Enquiry (Within 24 Hours)</option>
                          <option value="Immediate Same-Day Admission">Immediate / Same-Day Admission Required</option>
                          <option value="Planning for Next Week">Planning for Upcoming Week</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-[#18323A] uppercase tracking-wider mb-2">
                        Brief Context / Message (Confidential)
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        value={formState.message}
                        onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                        placeholder="Please share any key details (duration of symptoms, previous treatments, preferred centre)..."
                        className="w-full px-4 py-3 rounded-xl border border-[#D9E8E5] text-xs sm:text-sm focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition resize-none bg-[#F5FAF9]/30 text-[#18323A]"
                      ></textarea>
                    </div>

                    <div className="pt-3 flex justify-end border-t border-[#D9E8E5]">
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={isSubmitting}
                        className="w-full sm:w-auto"
                      >
                        {isSubmitting ? 'Sending Request...' : 'Submit Consultation Request'}
                      </Button>
                    </div>
                  </form>
                )}

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Interactive Google Map Section with Location Switcher */}
      <section className="w-full border-t border-[#D9E8E5] bg-[#F5FAF9] pt-16 pb-0">
        <div className="w-full px-4 sm:px-6 lg:px-8 mb-8">

          {/* Map Header & Location Selector */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#0F766E] block mb-1">
                LOCATION & DIRECTIONS
              </span>
              <h3 className="font-serif text-2xl sm:text-4xl text-[#123B5D] font-normal">
                Find Us on Google Maps
              </h3>
            </div>

            {/* Location Toggle Tabs */}
            <div className="inline-flex p-1 rounded-2xl bg-white border border-[#D9E8E5] shadow-sm">
              <button
                type="button"
                onClick={() => setSelectedMapLocation('clinic')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all ${selectedMapLocation === 'clinic'
                  ? 'bg-[#123B5D] text-white shadow-sm'
                  : 'text-[#64748B] hover:text-[#123B5D] hover:bg-[#F5FAF9]'
                  }`}
              >
                <Stethoscope className="w-4 h-4" />
                <span>UMED Clinic (OPD)</span>
              </button>

              <button
                type="button"
                onClick={() => setSelectedMapLocation('rehab')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all ${selectedMapLocation === 'rehab'
                  ? 'bg-[#123B5D] text-white shadow-sm'
                  : 'text-[#64748B] hover:text-[#123B5D] hover:bg-[#F5FAF9]'
                  }`}
              >
                <Building2 className="w-4 h-4" />
                <span>UMED Rehab Centre (24/7)</span>
              </button>
            </div>
          </div>

        </div>

        {/* Full-width Map Frame - Edge to Edge */}
        <div className="relative w-full h-[480px] sm:h-[550px] bg-slate-100 border-t border-[#D9E8E5] overflow-hidden">
          <iframe
            src={contactData.locations[0].mapEmbedUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            title="UMED Rehabilitation Centre Location Map"
            className={`absolute inset-0 w-full h-full transition-opacity duration-200 ${selectedMapLocation === 'rehab' ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
              }`}
          ></iframe>
          <iframe
            src={contactData.locations[1].mapEmbedUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            title="UMED Mental Health Clinic Location Map"
            className={`absolute inset-0 w-full h-full transition-opacity duration-200 ${selectedMapLocation === 'clinic' ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
              }`}
          ></iframe>
        </div>
      </section>
    </div>
  );
}
